import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { GroupDto } from '@mondy/shared';
import { api } from '@/api';

interface AcceptResponse {
  group: GroupDto;
  alreadyMember: boolean;
}

/**
 * Accept a group invite as a logged-in user whose email matches the invite.
 * Server returns the joined group (idempotent: sets `alreadyMember: true`
 * if you were already in the group).
 *
 * Invalidates `['groups','my']` and the standings / predictions caches;
 * the caller is responsible for setting the joined group as the active
 * scope via `useSetActiveGroup()`.
 */
export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api.post<AcceptResponse>(`/api/invites/${token}/accept`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['groups', 'my'] });
      void qc.invalidateQueries({ queryKey: ['standings'] });
      void qc.invalidateQueries({ queryKey: ['predictions'] });
    },
  });
}
